import React, { useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { useCoachChat } from './CoachChatContext'
import CandidateInfo from './CandidateInfo'
import { CALL_RING_TIMEOUT_MS } from '../services/CallService'

const CALL_RING_TIMEOUT_SECONDS = Math.ceil(CALL_RING_TIMEOUT_MS / 1000)

const CallCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
  background: ${(props) => (props.$isDarkMode ? '#1f2937' : '#fff')};
  border: 2px solid #007bff;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`

const CallIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #007bff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  }

  svg {
    width: 32px;
    height: 32px;
    color: white;
  }
`

const CallTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${(props) => (props.$isDarkMode ? '#f3f4f6' : '#495057')};
  margin-bottom: 0.5rem;
  text-align: center;
`

const CallMessage = styled.p`
  font-size: 1rem;
  color: ${(props) => (props.$isDarkMode ? '#d1d5db' : '#6c757d')};
  margin-bottom: 1.5rem;
  text-align: center;
  max-width: 400px;
  line-height: 1.5;
`

const CandidateInfoWrapper = styled.div`
  margin-bottom: 1.5rem;
  width: 100%;
  max-width: 400px;
`

const TimerSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 280px;
  margin-bottom: 0.5rem;
  /* Fixed height so Accept/Reject never shift as digits change */
  min-height: 4.25rem;
`

const TimerLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(props) => (props.$isDarkMode ? '#9ca3af' : '#6c757d')};
  margin-bottom: 0.35rem;
`

const TimerValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
  /* Reserve width for the widest digit string of the timeout (e.g. "30") */
  min-width: ${String(CALL_RING_TIMEOUT_SECONDS).length + 1}ch;
  text-align: center;
  color: ${(props) => props.$color};
  transition: color 0.3s ease;

  ${(props) =>
    props.$urgent
      ? `
    animation: timerUrgentPulse 0.8s ease-in-out infinite;

    @keyframes timerUrgentPulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.85; transform: scale(1.06); }
    }
  `
      : ''}
`

const ProgressTrack = styled.div`
  width: 100%;
  height: 6px;
  margin-top: 0.75rem;
  border-radius: 3px;
  background: ${(props) => (props.$isDarkMode ? '#374151' : '#e9ecef')};
  overflow: hidden;
`

const ProgressFill = styled.div`
  height: 100%;
  width: ${(props) => props.$percent}%;
  border-radius: 3px;
  background: ${(props) => props.$color};
  transition:
    width 0.2s linear,
    background-color 0.3s ease;
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`

const AcceptButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #218838;
  }

  &:active {
    background: #1e7e34;
  }
`

const RejectButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #c82333;
  }

  &:active {
    background: #bd2130;
  }
`

const getTimerColor = (secondsRemaining) => {
  if (secondsRemaining <= 5) return '#dc3545'
  if (secondsRemaining <= 10) return '#fd7e14'
  return '#007bff'
}

/**
 * IncomingCallCard component - displays when a candidate is calling
 * Based on the legacy acceptCallBox structure from the coach chat interface
 */
const IncomingCallCard = ({ candidate, onAcceptCall, onRejectCall }) => {
  const {
    chatManager,
    connectionState,
    pendingChats,
    isDarkMode,
    getDarkModeClasses,
  } = useCoachChat()

  const [secondsRemaining, setSecondsRemaining] = useState(
    CALL_RING_TIMEOUT_SECONDS,
  )
  const intervalRef = useRef(null)

  const clearCountdown = () => {
    if (intervalRef.current != null) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  // Countdown mirrors CallService CALL_RING_TIMEOUT_MS (CTS-9776).
  // Missed-call transition is still driven by candidate call_missed; this only
  // visualizes remaining time and cleans up on accept / decline / unmount / 0.
  useEffect(() => {
    const startedAt = Date.now()

    intervalRef.current = setInterval(() => {
      const elapsedMs = Date.now() - startedAt
      const next = Math.max(
        0,
        Math.ceil((CALL_RING_TIMEOUT_MS - elapsedMs) / 1000),
      )
      setSecondsRemaining(next)

      if (next <= 0) {
        clearCountdown()
      }
    }, 250)

    return () => {
      clearCountdown()
    }
  }, [])

  // Get candidate token for CandidateInfo
  // Try multiple sources: candidate.username, pendingChat.candidateToken, room API fields, or easyrtc.idToName
  const easyrtc = chatManager?.rtcService?.getEasyRTC()

  // First try to get from pendingChats (which should have candidateToken)
  const matchingPendingChat = candidate?.easyrtcid
    ? pendingChats?.find(
        (chat) =>
          chat.candidateId === candidate.easyrtcid ||
          chat.candidateEasyrtcid === candidate.easyrtcid,
      )
    : null

  const candidateToken =
    candidate?.username ||
    matchingPendingChat?.candidateToken ||
    (easyrtc &&
      candidate?.easyrtcid &&
      (easyrtc.getRoomApiField(
        connectionState?.chatRoom,
        candidate.easyrtcid,
        'chatToken',
      ) ||
        easyrtc.idToName(candidate.easyrtcid)))

  const handleAccept = () => {
    clearCountdown()
    onAcceptCall(candidate)
  }

  const handleReject = () => {
    clearCountdown()
    onRejectCall(candidate)
  }

  const resolvedDisplayName = chatManager
    ? chatManager.resolveCandidateDisplayName({
        ...candidate,
        username:
          candidate?.username || candidate?.candidateToken || candidateToken,
      })
    : candidate?.displayName || candidate?.username

  const timerColor = getTimerColor(secondsRemaining)
  const progressPercent = (secondsRemaining / CALL_RING_TIMEOUT_SECONDS) * 100

  return (
    <CallCardContainer $isDarkMode={isDarkMode}>
      <CallIcon>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z"
          />
        </svg>
      </CallIcon>

      <CallTitle $isDarkMode={isDarkMode}>Incoming Chat Request</CallTitle>

      <CallMessage $isDarkMode={isDarkMode}>
        {candidate?.chatMode === 'video' && 'Video chat request from'}
        {candidate?.chatMode === 'audio' && 'Audio chat request from'}
        {candidate?.chatMode === 'text' && 'Text chat request from'}{' '}
        {resolvedDisplayName}
      </CallMessage>

      {candidateToken ? (
        <CandidateInfoWrapper>
          <CandidateInfo chatToken={candidateToken} showEndChatButton={false} />
        </CandidateInfoWrapper>
      ) : (
        candidate && (
          <CandidateInfoWrapper>
            <div
              className={getDarkModeClasses('text-gray-700', 'text-gray-200')}
              style={{
                fontWeight: 600,
                marginBottom: '0.5rem',
              }}
            >
              {resolvedDisplayName}
            </div>
            <div
              className={getDarkModeClasses('text-gray-600', 'text-gray-300')}
              style={{ fontSize: '0.875rem' }}
            >
              <div>Chat Mode: {candidate.chatMode}</div>
              {candidate.browser && <div>Browser: {candidate.browser}</div>}
              {candidate.isMobile && <div>Mobile Device</div>}
              {candidate.locale && <div>Locale: {candidate.locale}</div>}
            </div>
          </CandidateInfoWrapper>
        )
      )}

      <TimerSection
        role="timer"
        aria-live="polite"
        aria-atomic="true"
        aria-label={`${secondsRemaining} seconds remaining to answer`}
      >
        <TimerLabel $isDarkMode={isDarkMode}>Time to answer</TimerLabel>
        <TimerValue
          $color={timerColor}
          $urgent={secondsRemaining <= 5 && secondsRemaining > 0}
        >
          {secondsRemaining}
        </TimerValue>
        <ProgressTrack $isDarkMode={isDarkMode}>
          <ProgressFill $percent={progressPercent} $color={timerColor} />
        </ProgressTrack>
      </TimerSection>

      <ButtonContainer>
        <AcceptButton onClick={handleAccept}>Accept</AcceptButton>
        <RejectButton onClick={handleReject}>Reject</RejectButton>
      </ButtonContainer>
    </CallCardContainer>
  )
}

export default IncomingCallCard
